import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, Pause, Play, CalendarDays, Trash2, X, ArrowLeft, Check, Gift, ExternalLink, LogOut } from "lucide-react";
import { api, TIER_MAP, TOKEN_KEY } from "@/lib/api";
import { AdminActionsBar } from "@/components/AdminActions";
import { T } from "@/lib/testIds";

const STATUSES = [
  { id: "", label: "All" },
  { id: "active", label: "Active" },
  { id: "paused", label: "Paused" },
  { id: "cancelled", label: "Cancelled" },
];
const FREQS = [
  { id: "", label: "All" },
  { id: "weekly", label: "Weekly" },
  { id: "fortnightly", label: "Fortnightly" },
];
const PAYMENT_FILTERS = [
  { id: "", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "customer_confirmed", label: "Awaiting verify" },
  { id: "verified", label: "Verified" },
];

export default function Admin() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [freq, setFreq] = useState("");
  const [payment, setPayment] = useState("");
  const [rescheduling, setRescheduling] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (freq) params.frequency = freq;
      if (payment) params.payment_status = payment;
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/subscriptions", { params }),
        api.get("/subscriptions/stats"),
      ]);
      setSubs(list); setStats(s);
    } catch { toast.error("Failed to load subscriptions."); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, status, freq, payment]);

  const setSubStatus = async (id, newStatus) => {
    try { await api.patch(`/subscriptions/${id}/status`, { status: newStatus }); toast.success(`Marked ${newStatus}.`); load(); }
    catch { toast.error("Update failed."); }
  };
  const doReschedule = async () => {
    if (!rescheduleDate) return;
    try { await api.patch(`/subscriptions/${rescheduling}/reschedule`, { next_delivery: rescheduleDate }); toast.success("Delivery rescheduled."); setRescheduling(null); setRescheduleDate(""); load(); }
    catch { toast.error("Reschedule failed."); }
  };
  const verifyPay = async (id) => {
    try { await api.patch(`/subscriptions/${id}/verify-payment`, { verified: true }); toast.success("Payment verified."); load(); }
    catch { toast.error("Verify failed."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this subscription permanently?")) return;
    try { await api.delete(`/subscriptions/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div data-testid={T.admin.root} className="min-h-screen bg-[var(--bg)]">
      <div className="border-b border-[var(--line-strong)] bg-[var(--bg-alt)]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="font-serif text-xl">BUDS<span className="italic">.</span></Link>
            <span className="hairline w-8" />
            <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)]">Studio · Admin</span>
          </div>
          <div className="flex items-center gap-8">
            <AdminActionsBar />
            <Link data-testid={T.admin.calendarLink} to="/admin/calendar" className="btn-ghost"><CalendarDays size={14}/> Delivery Calendar</Link>
            <Link to="/" className="btn-ghost"><ArrowLeft size={14} /> Back to site</Link>
            <button data-testid={T.admin.logout} onClick={() => { localStorage.removeItem(TOKEN_KEY); window.location.href = "/admin/login"; }} className="btn-ghost"><LogOut size={14}/> Log out</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="chapter-num text-5xl">§</p>
            <h1 className="font-serif text-5xl sm:text-6xl leading-none mt-2">Members</h1>
          </div>
          <p className="text-[var(--ink-soft)] text-sm max-w-sm text-right">Pause, resume, reschedule, verify prepayments, and maintain your active member roster.</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[var(--line-strong)] border border-[var(--line-strong)] mb-14">
            <Stat testid={T.admin.statMrr} label="Monthly recurring" value={`₹${(stats.mrr || 0).toLocaleString("en-IN")}`} />
            <Stat testid={T.admin.statActive} label="Active" value={stats.active} />
            <Stat testid={T.admin.statPaused} label="Paused" value={stats.paused} />
            <Stat testid={T.admin.statWeekly} label="Weekly" value={stats.weekly} />
            <Stat testid={T.admin.statFortnightly} label="Fortnightly" value={stats.fortnightly} />
            <Stat testid={T.admin.statPendingPayments} label="Payments to verify" value={stats.pending_payments || 0} accent />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-6 mb-8">
          <div className="flex items-center gap-3 border-b border-[var(--line-strong)] py-2 flex-1 min-w-[240px] max-w-md">
            <Search size={16} className="text-[var(--ink-soft)]" />
            <input data-testid={T.admin.search} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone or recipient"
              className="bg-transparent outline-none w-full text-sm" />
          </div>
          <Segmented testid={T.admin.filterStatus} label="Status" options={STATUSES} value={status} onChange={setStatus} />
          <Segmented testid={T.admin.filterFreq} label="Frequency" options={FREQS} value={freq} onChange={setFreq} />
          <Segmented label="Payment" options={PAYMENT_FILTERS} value={payment} onChange={setPayment} />
        </div>

        <div className="border border-[var(--line-strong)]">
          <div className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1fr_auto] gap-4 px-6 py-4 text-[10px] tracking-[0.22em] uppercase text-[var(--ink-soft)] border-b border-[var(--line-strong)] bg-[var(--bg-alt)]">
            <span>Member</span><span>Membership</span><span>Frequency</span><span>Next</span><span>Status</span><span>Payment</span><span className="text-right">Actions</span>
          </div>
          {loading && <div className="p-10 text-center text-sm text-[var(--ink-soft)]">Loading…</div>}
          {!loading && subs.length === 0 && (
            <div className="p-16 text-center">
              <p className="font-serif text-3xl mb-2">No members yet.</p>
              <p className="text-sm text-[var(--ink-soft)]">Once customers subscribe, they will appear here.</p>
            </div>
          )}
          {subs.map((s) => {
            const tier = TIER_MAP[s.tier];
            return (
              <div key={s.id} data-testid={T.admin.row(s.id)}
                className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1fr_auto] gap-4 px-6 py-5 border-b border-[var(--line)] items-center text-sm hover:bg-[var(--bg-alt)] transition-colors duration-300">
                <div>
                  <p className="font-serif text-lg leading-tight flex items-center gap-2">
                    {s.full_name}
                    {s.is_gift && <span title={`Gift for ${s.recipient_name}`} className="inline-flex items-center gap-1 text-[9px] tracking-[0.22em] uppercase border border-[var(--sage-deep)] text-[var(--sage-deep)] px-1.5 py-0.5"><Gift size={10}/> Gift</span>}
                  </p>
                  <p className="text-xs text-[var(--ink-soft)] mt-0.5">{s.email} · {s.phone}</p>
                  <p className="text-xs text-[var(--ink-soft)] mt-0.5 line-clamp-1">{s.address}</p>
                  {s.is_gift && <p className="text-[11px] text-[var(--sage-deep)] mt-0.5 italic">→ {s.recipient_name}</p>}
                </div>
                <div>
                  <p className="font-serif">{tier?.name || s.tier}</p>
                  <p className="text-xs text-[var(--ink-soft)]">₹{s.weekly_price}/wk</p>
                </div>
                <div className="uppercase text-[11px] tracking-[0.2em]">{s.frequency}</div>
                <div className="text-xs">{formatDate(s.next_delivery)}</div>
                <div><StatusPill status={s.status} /></div>
                <div><PaymentPill status={s.payment_status} amount={s.first_payment_amount}/></div>
                <div className="flex items-center gap-2 justify-end">
                  {s.payment_status === "customer_confirmed" && (
                    <IconBtn testid={T.admin.verifyPayment(s.id)} label="Verify payment" onClick={() => verifyPay(s.id)}><Check size={14}/></IconBtn>
                  )}
                  {s.status === "active" && (
                    <IconBtn testid={T.admin.pause(s.id)} label="Pause" onClick={() => setSubStatus(s.id, "paused")}><Pause size={14} /></IconBtn>
                  )}
                  {s.status === "paused" && (
                    <IconBtn testid={T.admin.resume(s.id)} label="Resume" onClick={() => setSubStatus(s.id, "active")}><Play size={14} /></IconBtn>
                  )}
                  <IconBtn testid={T.admin.reschedule(s.id)} label="Reschedule" onClick={() => { setRescheduling(s.id); setRescheduleDate(s.next_delivery || ""); }}><CalendarDays size={14} /></IconBtn>
                  {s.status !== "cancelled" && (
                    <IconBtn testid={T.admin.cancel(s.id)} label="Cancel" onClick={() => setSubStatus(s.id, "cancelled")}><X size={14} /></IconBtn>
                  )}
                  <IconBtn label="Delete" onClick={() => remove(s.id)}><Trash2 size={14} /></IconBtn>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {rescheduling && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setRescheduling(null)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()}
              className="bg-[var(--bg)] border border-[var(--ink)] p-10 w-full max-w-md">
              <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-3">Reschedule delivery</p>
              <h3 className="font-serif text-3xl mb-6">Pick a new date</h3>
              <input data-testid={T.admin.rescheduleInput} type="date" value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)} className="editorial-input" />
              <div className="flex justify-end gap-4 mt-8">
                <button className="btn-ghost" onClick={() => setRescheduling(null)}>Cancel</button>
                <button data-testid={T.admin.rescheduleSave} className="btn-primary" onClick={doReschedule}>Save</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className="p-6" style={{ background: accent && Number(value) > 0 ? "var(--blush)" : "var(--bg)" }}>
      <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)] mb-3">{label}</p>
      <p className="font-serif text-4xl leading-none">{value}</p>
    </div>
  );
}
function Segmented({ label, options, value, onChange, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-3">
      <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)]">{label}</span>
      <div className="flex border border-[var(--line-strong)]">
        {options.map((o) => (
          <button key={o.id || "all"} onClick={() => onChange(o.id)}
            className="px-3 py-1.5 text-[11px] tracking-[0.16em] uppercase transition-colors duration-300"
            style={{ background: value === o.id ? "var(--ink)" : "transparent", color: value === o.id ? "var(--bg)" : "var(--ink)" }}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    active: { bg: "var(--ink)", color: "var(--bg)", label: "Active" },
    paused: { bg: "var(--blush)", color: "var(--ink)", label: "Paused" },
    cancelled: { bg: "transparent", color: "var(--ink-soft)", label: "Cancelled" },
  };
  const s = map[status] || map.active;
  return (
    <span className="inline-block text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border"
      style={{ background: s.bg, color: s.color, borderColor: status === "cancelled" ? "var(--line-strong)" : s.bg }}>
      {s.label}
    </span>
  );
}
function PaymentPill({ status, amount }) {
  const map = {
    pending: { color: "var(--ink-soft)", label: "Awaiting", border: "var(--line-strong)", bg: "transparent" },
    customer_confirmed: { color: "var(--ink)", label: "Verify", border: "var(--ink)", bg: "var(--blush)" },
    verified: { color: "var(--bg)", label: "Verified", border: "var(--ink)", bg: "var(--ink)" },
    failed: { color: "var(--bg)", label: "Failed", border: "var(--ink)", bg: "var(--ink-soft)" },
  };
  const s = map[status] || map.pending;
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-block text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border w-fit"
        style={{ color: s.color, borderColor: s.border, background: s.bg }}>
        {s.label}
      </span>
      <span className="text-[11px] text-[var(--ink-soft)]">₹{(amount||0).toLocaleString("en-IN")}</span>
    </div>
  );
}
function IconBtn({ children, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} title={label} aria-label={label}
      className="w-8 h-8 flex items-center justify-center border border-[var(--line-strong)] hover:border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors duration-300">
      {children}
    </button>
  );
}
